import { useId, useState, type FC, type ReactNode } from 'react';
import { cn } from '../lib/utils';
import { ChatPanelHeader } from './chat-panel-header';
import type { ChatPanelMode } from './chat-panel-mode';
import { ChatPanelResizeHandle } from './chat-panel-resize-handle';
import { ChatPanelTab } from './chat-panel-tab';

interface ChatPanelProps {
  mode: ChatPanelMode;
  onModeChange: (mode: ChatPanelMode) => void;
  /** Docked width in px. Ignored in full screen and on phones. */
  width?: number;
  onWidthChange?: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
  title?: string;
  /** One line under the title naming what the Assistant can see. */
  context?: ReactNode;
  /** Count on the collapsed tab. */
  badge?: number;
  badgeLabel?: string;
  /** Header controls before the mode buttons. */
  actions?: ReactNode;
  /** The conversation column: a transcript or overview, then a composer. */
  children: ReactNode;
  /** Full screen only: the context column to the right of the conversation. */
  aside?: ReactNode;
  /** `fixed` pins to the viewport; `absolute` fills a positioned parent. */
  position?: 'fixed' | 'absolute';
  className?: string;
}

/**
 * The Assistant's frame. Docked it is a resizable glass column on the right
 * edge; full screen it takes the viewport and adds the `aside` column;
 * collapsed it shrinks to a tab. Content is entirely the caller's.
 */
export const ChatPanel: FC<ChatPanelProps> = ({
  mode,
  onModeChange,
  width = 440,
  onWidthChange,
  minWidth = 360,
  maxWidth = 820,
  title = 'Assistant',
  context,
  badge,
  badgeLabel,
  actions,
  children,
  aside,
  position = 'fixed',
  className
}) => {
  const titleId = useId();
  const [isDragging, setIsDragging] = useState(false);
  const positionClass = position === 'fixed' ? 'fixed' : 'absolute';

  if (mode === 'collapsed') {
    return (
      <div
        className={cn(positionClass, 'inset-y-0 right-0 z-30 max-sm:contents', className)}
      >
        <ChatPanelTab
          label={title}
          badge={badge}
          badgeLabel={badgeLabel}
          onOpen={() => onModeChange('docked')}
        />
      </div>
    );
  }

  const isFull = mode === 'full';

  return (
    <aside
      aria-labelledby={titleId}
      data-mode={mode}
      style={isFull ? undefined : { width }}
      onKeyDown={(event) => {
        // Popovers (selects, tooltips) are portalled but still bubble
        // here through React; their Escape belongs to them.
        const fromPopover =
          event.target instanceof Element &&
          event.target.closest('[data-radix-popper-content-wrapper]');
        if (event.key === 'Escape' && isFull && !event.defaultPrevented && !fromPopover) {
          onModeChange('docked');
        }
      }}
      className={cn(
        positionClass,
        'inset-y-0 right-0 z-30 flex flex-col overflow-hidden border-l border-border-strong bg-[var(--z-surface-overlay)] shadow-2xl backdrop-blur-[28px] backdrop-saturate-[1.15]',
        'max-sm:w-full!',
        isFull && 'w-full border-l-0',
        !isDragging &&
          'transition-[width] duration-[280ms] ease-emphasized motion-reduce:transition-none',
        className
      )}
    >
      {!isFull && onWidthChange ? (
        <ChatPanelResizeHandle
          width={width}
          minWidth={minWidth}
          maxWidth={maxWidth}
          onWidthChange={onWidthChange}
          onDraggingChange={setIsDragging}
          label={`Resize ${title} panel`}
        />
      ) : null}
      <ChatPanelHeader
        title={title}
        titleId={titleId}
        context={context}
        mode={mode}
        onModeChange={onModeChange}
        actions={actions}
      />
      <div className="flex min-h-0 flex-1">
        <div
          className={cn(
            'flex min-h-0 min-w-0 flex-1 flex-col',
            isFull && aside && 'border-r border-[var(--z-border-subtle)]'
          )}
        >
          {children}
        </div>
        {isFull && aside ? (
          <div className="flex min-h-0 w-[420px] shrink-0 flex-col max-md:hidden">
            {aside}
          </div>
        ) : null}
      </div>
    </aside>
  );
};
